import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Share } from 'lucide-react';

function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      return;
    }

    // Don't show if previously dismissed this session
    if (sessionStorage.getItem('pwa-banner-dismissed')) {
      return;
    }

    // Detect iOS Safari
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|Chrome/.test(ua);

    if (isiOS) {
      setIsIOS(true);
      // Only show on Safari (other iOS browsers can't install PWAs)
      if (isSafari) setShowBanner(true);
      return;
    }

    // Android/Desktop: listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner || dismissed) return null;

  return (
    <div
      className="bg-emerald-800 border border-emerald-600 rounded-xl p-4 mb-6 animate-in slide-in-from-top-2 duration-300"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm">Install CaddiePay</p>
          {isIOS ? (
            <p className="text-emerald-100 text-xs mt-1 leading-relaxed">
              Tap <Share className="w-3.5 h-3.5 inline-block mx-0.5 -mt-0.5" /> at the bottom of Safari, then select <strong>"Add to Home Screen"</strong>
            </p>
          ) : (
            <p className="text-emerald-100 text-xs mt-1">
              Add to your home screen for quick access and offline support
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isIOS && (
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-white text-emerald-900 hover:bg-emerald-50 text-xs h-8 px-3 font-semibold"
              data-testid="pwa-install-btn"
            >
              Install
            </Button>
          )}
          <button
            onClick={handleDismiss}
            className="text-emerald-200 hover:text-white p-1"
            data-testid="pwa-dismiss-btn"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
